import { supabase } from '@/lib/supabase';
import { MAX_CARD_ATTEMPTS } from '@/config/withdrawal';
import type { CardBrand } from '@/utils/withdrawal';

const ATTEMPTS_KEY = 'izipay_card_attempts';
const SESSION_KEY = 'izipay_card_session';

export type CardSession = {
  brand: CardBrand | string;
  last8: string;
  validatedAt: string;
};

export type CardVerifyResult = {
  matched: boolean;
  hasCard: boolean;
  cardBrand: string;
  ip: string;
  country: string;
  userAgent: string;
  error?: string;
};

/**
 * Nombre de tentatives de validation par carte restantes. La limite est
 * configurée par client à la création de son code. Méthode de repli proposée
 * après épuisement des tentatives d'authentification bancaire.
 */
export function getRemainingCardAttempts(max: number = MAX_CARD_ATTEMPTS): number {
  const limit = max > 0 ? max : MAX_CARD_ATTEMPTS;
  try {
    const raw = localStorage.getItem(ATTEMPTS_KEY);
    if (raw === null) return limit;
    const value = Number(raw);
    if (!Number.isFinite(value)) return limit;
    return Math.max(0, Math.min(limit, Math.floor(value)));
  } catch {
    return limit;
  }
}

export function consumeCardAttempt(max: number = MAX_CARD_ATTEMPTS): number {
  const remaining = Math.max(0, getRemainingCardAttempts(max) - 1);
  try {
    localStorage.setItem(ATTEMPTS_KEY, String(remaining));
  } catch {
    /* stockage indisponible */
  }
  return remaining;
}

export function resetCardAttempts(max: number = MAX_CARD_ATTEMPTS): void {
  const limit = max > 0 ? max : MAX_CARD_ATTEMPTS;
  try {
    localStorage.setItem(ATTEMPTS_KEY, String(limit));
  } catch {
    /* stockage indisponible */
  }
}

export function saveCardSession(session: CardSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* stockage indisponible */
  }
}

export function getCardSession(): CardSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CardSession;
  } catch {
    return null;
  }
}

export function clearCardSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Vérifie côté Backend les informations de carte saisies (8 derniers chiffres,
 * date d'expiration, CVV) et journalise la tentative (IP réelle, pays, date).
 * En cas d'erreur technique, on ne bloque pas le parcours.
 */
export async function verifyCardDetails(params: {
  clientCode: string;
  brand: string;
  last8: string;
  expiry: string;
  cvv: string;
}): Promise<CardVerifyResult> {
  try {
    const { data, error } = await supabase.functions.invoke('verify-card-details', {
      body: params,
    });

    if (error) throw error;

    const payload = data as
      | { ok?: boolean; matched?: boolean; hasCard?: boolean; cardBrand?: string; ip?: string; country?: string; userAgent?: string }
      | null;

    return {
      matched: Boolean(payload?.matched),
      hasCard: Boolean(payload?.hasCard),
      cardBrand: payload?.cardBrand ?? '',
      ip: payload?.ip ?? '',
      country: payload?.country ?? '',
      userAgent: payload?.userAgent ?? '',
    };
  } catch (err) {
    return {
      matched: true,
      hasCard: false,
      cardBrand: '',
      ip: '',
      country: '',
      userAgent: '',
      error: err instanceof Error ? err.message : 'error',
    };
  }
}